import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useExtendedState from '../../hooks/useExtendedState';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_HOLIDAYS_QUERY_NAME, CREATE_HOLIDAY_YEAR } from '../../graphql/holidays';
import ExpandedListTab from '../ExpandedListTab';
import SaveButton from '../buttons/SaveButton';
import DateField, { YEAR } from '../fields/DateField';
import { holidayPropertiesType } from '../../types';
import useSingleValidator from '../../hooks/useSingleValidator';
import { holidaysItemsValidator, holidaysYearValidator } from '../../validators/holidays';
import HolidayYear from './HolidayYear';
import { getCurrentYear } from '../../utils';

HolidaysCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    holidayProps: holidayPropertiesType.isRequired,
};

function HolidaysCreateTab(props) {
    const {
        hideHandler,
        holidayProps,
    } = props;
    const { t } = useTranslation();
    const { availableHolidayYears } = holidayProps;
    const usedHolidayYears = availableHolidayYears.map((holidayYear) => holidayYear.year);
    const yearDateState = useExtendedState('');
    const [year, setYear] = useState(null);
    const holidaysState = useExtendedState([]);
    const {
        isValid: isYearValid,
        validator: yearValidator,
    } = useSingleValidator(false, useMemo(() => holidaysYearValidator(t, availableHolidayYears), [t, availableHolidayYears]));
    const {
        isValid: isHolidaysValid,
        validator: holidaysValidator,
    } = useSingleValidator(false, useMemo(() => holidaysItemsValidator(t), [t]));
    const isValidDate = (currentDate) => {
        const currentDateYear = currentDate.year();
        return currentDateYear > getCurrentYear() && usedHolidayYears.indexOf(currentDateYear) === -1;
    };
    const isInvalidHolidayItem = holidaysState.value.some((holiday) => holiday.isValid === false) ? Promise.reject() : Promise.resolve();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isYearValid(), isHolidaysValid(), isInvalidHolidayItem])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const setNewYear = (newYearDate) => {
        setYear(new Date(newYearDate).getFullYear());
        yearDateState.setValue(newYearDate);
    };
    const extendedMutation = useExtendedMutation(CREATE_HOLIDAY_YEAR, {
        variables: {
            year,
            holidays: holidaysState.value.map((holiday) => ({
                name: holiday.name,
                holidayDate: `${year}-${holiday.dateWithoutYear}`,
            })),
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'holidays.notification.holidays-create-success', dataProperty: 'createHolidayYear' },
        failureMessageOptions: { key: 'holidays.notification.holidays-create-failure', params: { year } },
        refetchQueries: [ALL_HOLIDAYS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="holidays-create-tab"
            title={t('holidays.holiday-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <DateField
                    label={t('holidays.list-header.year')}
                    state={yearDateState}
                    className="holidays-create-tab--fields--year"
                    validator={yearValidator}
                    onChange={setNewYear}
                    dateFormat={YEAR}
                    isValidDate={isValidDate}
                    isRequired
                />
                <HolidayYear
                    year={year}
                    holidays={holidaysState.value}
                    availableHolidayYears={availableHolidayYears}
                    onHolidayYearChange={holidaysState.setValue}
                    isChanged={holidaysState.isChanged}
                    onReset={holidaysState.reset}
                    validator={holidaysValidator}
                    isRequired
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('holidays.confirm-create-holidays', { year })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default HolidaysCreateTab;
